import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import { useAuth } from './AuthContext';
import { logger } from '../utils/logger';

const BusinessContext = createContext(null);

const DEFAULT_BUSINESSES = [
  { name: 'SRLP', shortCode: 'SRLP', color: '#1E3A5F', icon: 'briefcase' },
  { name: 'P Law', shortCode: 'PLAW', color: '#B4233A', icon: 'scale' },
  { name: 'P Pers', shortCode: 'PPERS', color: '#10B981', icon: 'users' },
  { name: 'JVs', shortCode: 'JVS', color: '#B45309', icon: 'handshake' },
];

export function BusinessProvider({ children }) {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [activeBusiness, setActiveBusiness] = useState(null); // null = all
  const [loading, setLoading] = useState(false);

  const fetchBusinesses = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await api.get('/businesses');
      setBusinesses(res.data);
    } catch (err) {
      logger.error('Failed to fetch businesses:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchBusinesses();
  }, [user, fetchBusinesses]);

  const createBusiness = useCallback(async (data) => {
    const res = await api.post('/businesses', data);
    setBusinesses((prev) => [...prev, res.data]);
    return res.data;
  }, []);

  const updateBusiness = useCallback(async (bizId, data) => {
    const res = await api.put(`/businesses/${bizId}`, data);
    setBusinesses((prev) => prev.map((b) => (b.id === bizId ? res.data : b)));
    return res.data;
  }, []);

  const deleteBusiness = useCallback(async (bizId) => {
    await api.delete(`/businesses/${bizId}`);
    setBusinesses((prev) => prev.filter((b) => b.id !== bizId));
    setActiveBusiness((curr) => (curr?.id === bizId ? null : curr));
  }, []);

  const createDefaultBusinesses = useCallback(async () => {
    const created = [];
    for (const biz of DEFAULT_BUSINESSES) {
      const res = await api.post('/businesses', biz);
      created.push(res.data);
    }
    setBusinesses(created);
    return created;
  }, []);

  // Derive lookup helpers from the businesses array — memoized so consumers don't
  // re-render when unrelated state changes.
  const helpers = useMemo(() => {
    const byId = new Map(businesses.map((b) => [b.id, b]));
    return {
      getBusinessById: (id) => byId.get(id),
      getBusinessColor: (id) => byId.get(id)?.color || '#94A3B8',
      getBusinessShortCode: (id) => byId.get(id)?.shortCode || '',
    };
  }, [businesses]);

  const value = useMemo(
    () => ({
      businesses,
      activeBusiness,
      setActiveBusiness,
      loading,
      fetchBusinesses,
      createBusiness,
      updateBusiness,
      deleteBusiness,
      createDefaultBusinesses,
      ...helpers,
      DEFAULT_BUSINESSES,
    }),
    [
      businesses, activeBusiness, loading,
      fetchBusinesses, createBusiness, updateBusiness, deleteBusiness,
      createDefaultBusinesses, helpers,
    ],
  );

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
}

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) throw new Error('useBusiness must be used within BusinessProvider');
  return context;
};
